const resolveApiBase = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    if (host === 'innovher.com' || host === 'www.innovher.com') {
      if (!envUrl || envUrl.includes('localhost') || envUrl.includes('127.0.0.1')) {
        return 'https://innovher.ideaindia.co.in/api';
      }
    }
  }
  return envUrl || 'http://localhost:5001/api';
};
const API_BASE = resolveApiBase();
const ACCEL_BASE = `${API_BASE}/accelerator`;

function getToken() {
  return localStorage.getItem('innovher_token');
}

function getHeaders(isJson = true) {
  const headers = {};
  if (isJson) headers['Content-Type'] = 'application/json';
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function request(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: { ...getHeaders(options.body && !(options.body instanceof FormData)), ...options.headers }
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

function connectGoogleCalendar() {
  const token = getToken();
  const url = API_BASE.replace('/api', '') + `/auth/google?token=${token}`;
  
  // Calculate center of screen for popup
  const width = 500;
  const height = 600;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;
  
  window.open(url, 'Google Calendar OAuth', `width=${width},height=${height},left=${left},top=${top}`);
}

async function disconnectGoogleCalendar() {
  const url = API_BASE.replace('/api', '') + `/auth/google/disconnect`;
  await fetch(url, { method: 'POST', headers: getHeaders() });
}

export const api = {
  uploadUrl: (path) => `${API_BASE}${path}`,
  connectGoogleCalendar,
  disconnectGoogleCalendar,
  login: (email, password) =>
    request(`${ACCEL_BASE}/auth/login`, { method: 'POST', body: JSON.stringify({ email, password }) }),

  me: () => request(`${ACCEL_BASE}/auth/me`),

  changePassword: (currentPassword, newPassword) =>
    request(`${ACCEL_BASE}/auth/change-password`, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword })
    }),

  forgotPassword: (email) =>
    request(`${ACCEL_BASE}/auth/forgot-password`, { method: 'POST', body: JSON.stringify({ email }) }),

  resetPassword: (token, newPassword) =>
    request(`${ACCEL_BASE}/auth/reset-password`, { method: 'POST', body: JSON.stringify({ token, newPassword }) }),

  apply: (body) =>
    request(`${ACCEL_BASE}/startups/apply`, { method: 'POST', body: JSON.stringify(body) }),

  getAnalytics: () => request(`${ACCEL_BASE}/analytics`),

  getStartups: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`${ACCEL_BASE}/startups${qs ? `?${qs}` : ''}`);
  },

  getStartup: (id) => request(`${ACCEL_BASE}/startups/${id}`),

  updateStartup: (id, body) =>
    request(`${ACCEL_BASE}/startups/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  acceptStartup: (id, body = {}) =>
    request(`${ACCEL_BASE}/startups/${id}/accept`, { method: 'POST', body: JSON.stringify(body) }),

  rejectStartup: (id, body = {}) =>
    request(`${ACCEL_BASE}/startups/${id}/reject`, { method: 'POST', body: JSON.stringify(body) }),

  onboardStartup: (id, body = {}) =>
    request(`${ACCEL_BASE}/startups/${id}/onboard`, { method: 'POST', body: JSON.stringify(body) }),

  submitQuestionnaire: (id, questionnaireData) =>
    request(`${ACCEL_BASE}/startups/${id}/submit-questionnaire`, {
      method: 'POST',
      body: JSON.stringify({ questionnaireData })
    }),

  assignMentor: (startupId, mentorId) =>
    request(`${ACCEL_BASE}/startups/${startupId}/assign-mentor`, {
      method: 'POST',
      body: JSON.stringify({ mentorId })
    }),

  removeMentor: (startupId, mentorId) =>
    request(`${ACCEL_BASE}/startups/${startupId}/remove-mentor`, {
      method: 'POST',
      body: JSON.stringify({ mentorId })
    }),

  shareDocumentWithMentor: (docId, mentorId) =>
    request(`${ACCEL_BASE}/documents/${docId}/share-mentor`, {
      method: 'POST',
      body: JSON.stringify({ mentorId })
    }),

  revokeDocumentFromMentor: (docId, mentorId) =>
    request(`${ACCEL_BASE}/documents/${docId}/revoke-mentor`, {
      method: 'POST',
      body: JSON.stringify({ mentorId })
    }),

  getQuestionnaire: () => request(`${ACCEL_BASE}/questionnaire`),

  getApplyQuestionnaire: () => request(`${ACCEL_BASE}/questionnaire/apply`),

  updateQuestionnaire: (body) =>
    request(`${ACCEL_BASE}/questionnaire`, { method: 'PUT', body: JSON.stringify(body) }),

  getEvents: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`${ACCEL_BASE}/events${qs ? `?${qs}` : ''}`);
  },

  getCalendarToken: () => request(`${ACCEL_BASE}/calendar-token`),


  createEvent: (body) =>
    request(`${ACCEL_BASE}/events`, { method: 'POST', body: JSON.stringify(body) }),

  updateEvent: (id, body) =>
    request(`${ACCEL_BASE}/events/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  getDocuments: (params = {}) => {
    let queryParams = params;
    if (typeof params === 'string') {
      queryParams = { startupId: params };
    }
    const clean = Object.fromEntries(
      Object.entries(queryParams || {}).filter(([_, v]) => v != null && v !== '')
    );
    const qs = new URLSearchParams(clean).toString();
    return request(`${ACCEL_BASE}/documents${qs ? `?${qs}` : ''}`);
  },

  createDocument: (body) =>
    request(`${ACCEL_BASE}/documents`, { method: 'POST', body: JSON.stringify(body) }),

  createDocumentsBulk: (body) =>
    request(`${ACCEL_BASE}/documents/bulk`, { method: 'POST', body: JSON.stringify(body) }),

  updateDocument: (id, body) =>
    request(`${ACCEL_BASE}/documents/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  uploadDocument: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return request(`${ACCEL_BASE}/documents/${id}/upload`, { method: 'POST', body: form });
  },

  uploadLogo: (startupId, file) => {
    const form = new FormData();
    form.append('logo', file);
    return request(`${ACCEL_BASE}/startups/${startupId}/logo`, { method: 'POST', body: form });
  },

  getTasks: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`${ACCEL_BASE}/tasks${qs ? `?${qs}` : ''}`);
  },

  createTask: (body) =>
    request(`${ACCEL_BASE}/tasks`, { method: 'POST', body: JSON.stringify(body) }),

  updateTask: (id, body) =>
    request(`${ACCEL_BASE}/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteTask: (id) =>
    request(`${ACCEL_BASE}/tasks/${id}`, { method: 'DELETE' }),

  getTickets: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`${ACCEL_BASE}/tickets${qs ? `?${qs}` : ''}`);
  },

  createTicket: (body) =>
    request(`${ACCEL_BASE}/tickets`, { method: 'POST', body: JSON.stringify(body) }),

  updateTicket: (id, body) =>
    request(`${ACCEL_BASE}/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  getTimeLedger: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`${ACCEL_BASE}/time-ledger${qs ? `?${qs}` : ''}`);
  },

  getUsers: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`${ACCEL_BASE}/users${qs ? `?${qs}` : ''}`);
  },

  createUser: (body) =>
    request(`${ACCEL_BASE}/users`, { method: 'POST', body: JSON.stringify(body) }),

  updateUser: (id, body) =>
    request(`${ACCEL_BASE}/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  getDealflow: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`${ACCEL_BASE}/dealflow${qs ? `?${qs}` : ''}`);
  },

  createDealflow: (body) =>
    request(`${ACCEL_BASE}/dealflow`, { method: 'POST', body: JSON.stringify(body) }),

  updateDealflow: (id, body) =>
    request(`${ACCEL_BASE}/dealflow/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  getAuditLogs: () => request(`${ACCEL_BASE}/audit-logs`),

  getBatches: () => request(`${ACCEL_BASE}/batches`),

  createBatch: (body) =>
    request(`${ACCEL_BASE}/batches`, { method: 'POST', body: JSON.stringify(body) }),

  updateBatch: (id, body) =>
    request(`${ACCEL_BASE}/batches/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  getBatch: (id) => request(`${ACCEL_BASE}/batches/${id}`),

  importBatchStartups: (batchId, startups) =>
    request(`${ACCEL_BASE}/batches/${batchId}/startups`, {
      method: 'POST',
      body: JSON.stringify(Array.isArray(startups) ? { startups } : startups)
    }),

  assignBatchStartups: (batchId, startupIds, notifyFounders = true) =>
    request(`${ACCEL_BASE}/batches/${batchId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ startupIds, notifyFounders })
    }),

  getDeliverables: (startupId) => request(`${ACCEL_BASE}/startups/${startupId}/deliverables`),

  createDeliverable: (startupId, body) =>
    request(`${ACCEL_BASE}/startups/${startupId}/deliverables`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),

  updateDeliverable: (id, body) =>
    request(`${ACCEL_BASE}/deliverables/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteDeliverable: (id) =>
    request(`${ACCEL_BASE}/deliverables/${id}`, { method: 'DELETE' }),

  addDeliverableDocument: (deliverableId, { file, heading, action, signatureRequired }) => {
    const formData = new FormData();
    if (file) formData.append('file', file);
    if (heading) formData.append('heading', heading);
    if (action) formData.append('action', action);
    if (signatureRequired) formData.append('signatureRequired', 'true');
    return request(`${ACCEL_BASE}/deliverables/${deliverableId}/documents`, {
      method: 'POST',
      body: formData
    });
  },

  getStartupProfile: (id) => request(`${ACCEL_BASE}/startups/${id}/profile`),

  getKpis: (startupId) => request(`${ACCEL_BASE}/startups/${startupId}/kpis`),

  submitKpi: (startupId, body) =>
    request(`${ACCEL_BASE}/startups/${startupId}/kpis`, { method: 'POST', body: JSON.stringify(body) }),

  viewDocument: async (docId) => {
    const res = await fetch(`${ACCEL_BASE}/documents/${docId}/download`, {
      headers: getHeaders(false)
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'View failed');
    }
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await res.json();
      if (data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
        return;
      }
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },

  downloadDocument: async (docId, fallbackName) => {
    const res = await fetch(`${ACCEL_BASE}/documents/${docId}/download`, {
      headers: getHeaders(false)
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Download failed');
    }
    
    const contentType = res.headers.get('content-type') || '';
    
    if (contentType.includes('application/json')) {
      const data = await res.json();
      if (data.url) {
        try {
          const remoteRes = await fetch(data.url);
          const blob = await remoteRes.blob();
          const extMatch = data.url.match(/\.([a-z0-9]+)(?:[\?#]|$)/i);
          const ext = extMatch ? `.${extMatch[1]}` : '';
          let dlName = data.filename || fallbackName || 'document';
          if (!dlName.endsWith(ext)) dlName += ext;
          
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = dlName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } catch (err) {
          window.open(data.url, '_blank');
        }
        return;
      }
    }
    
    const disposition = res.headers.get('content-disposition');
    let filename = fallbackName || 'document';
    if (disposition && disposition.indexOf('attachment') !== -1) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
        }
    } else {
        if (!filename.includes('.')) {
             if (contentType.includes('pdf')) filename += '.pdf';
             else if (contentType.includes('jpeg')) filename += '.jpg';
             else if (contentType.includes('png')) filename += '.png';
             else if (contentType.includes('zip')) filename += '.zip';
        }
    }
    
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  uploadUrl: (path) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const base = API_BASE.replace('/api', '');
    return `${base}${path}`;
  }
};

export const STARTUP_STATUSES = [
  'REQUESTED', 'INVITED', 'QUESTIONNAIRE_SUBMITTED', 'UNDER_REVIEW',
  'LEGAL_ONBOARDING', 'ONBOARDED', 'REJECTED'
];

export const ONBOARDING_STATUSES = [
  'INVITED', 'QUESTIONNAIRE_SUBMITTED', 'UNDER_REVIEW', 'LEGAL_ONBOARDING'
];

export const isOnboardingStatus = (status) =>
  ONBOARDING_STATUSES.includes(status) || status === 'INVITED';

export const getStartupProfilePath = (startup) => {
  const id = startup?._id || startup;
  if (!id) return '/admin/onboarding';
  if (startup?.status === 'ONBOARDED') return `/admin/onboarded/startup/${id}`;
  if (ONBOARDING_STATUSES.includes(startup?.status)) return `/admin/onboarding/${id}`;
  return `/admin/onboarding/${id}`;
};

export const STATUS_LABELS = {
  REQUESTED: 'Requested',
  INVITED: 'Invited',
  QUESTIONNAIRE_SUBMITTED: 'Questionnaire Submitted',
  UNDER_REVIEW: 'Under Review',
  LEGAL_ONBOARDING: 'Legal Onboarding',
  ONBOARDED: 'Onboarded',
  REJECTED: 'Rejected'
};

export const STATUS_COLORS = {
  REQUESTED: 'bg-blue-500/20 text-blue-300',
  INVITED: 'bg-purple-500/20 text-purple-300',
  QUESTIONNAIRE_SUBMITTED: 'bg-yellow-500/20 text-yellow-300',
  UNDER_REVIEW: 'bg-orange-500/20 text-orange-300',
  LEGAL_ONBOARDING: 'bg-cyan-500/20 text-cyan-300',
  ONBOARDED: 'bg-green-500/20 text-green-300',
  REJECTED: 'bg-red-500/20 text-red-300'
};
